import { useEffect, useMemo, useState } from "react";
import { FileDown, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, money } from "../api";
import RatesPanel from "../components/RatesPanel";
import CompanyScopeBar from "../components/CompanyScopeBar";

export default function Finance({ company, setCompany }) {
  const [currency, setCurrency] = useState("TRY");
  const [pl, setPl] = useState({ rows: [], totals: {} });
  const [cf, setCf] = useState([]);
  const [vat, setVat] = useState(null);
  const [tick, setTick] = useState(0);
  const [openMonth, setOpenMonth] = useState(null);
  const [plEmp, setPlEmp] = useState("");
  const [plSort, setPlSort] = useState({ key: "project", dir: "asc" });

  const cq = company ? `&company=${encodeURIComponent(company)}` : "";
  useEffect(() => {
    api.get(`/reports/profit-loss?currency=${currency}${cq}`).then(r => setPl(r.data)).catch(() => toast.error("Kâr/zarar yüklenemedi"));
    api.get(`/reports/cashflow?currency=${currency}${cq}`).then(r => setCf(r.data)).catch(() => {});
    api.get(`/reports/vat?currency=${currency}${cq}`).then(r => setVat(r.data)).catch(() => {});
  }, [currency, tick, company]);

  const t = pl.totals || {};
  const pdf = async () => { try { await downloadFile(`/reports/pdf/portfolio?currency=${currency}`, `portfoy-kar-zarar-${currency}.pdf`); toast.success("PDF indirildi"); } catch { toast.error("PDF oluşturulamadı"); } };

  const plEmployers = useMemo(() => [...new Set((pl.rows || []).map(r => r.employer).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [pl.rows]);
  const plToggle = (key) => setPlSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const PlIco = ({ col }) => plSort.key !== col ? <ArrowUpDown size={11} /> : (plSort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />);
  const plRows = useMemo(() => {
    let arr = (pl.rows || []).filter(r => !plEmp || r.employer === plEmp);
    const k = plSort.key;
    arr = [...arr].sort((a, b) => {
      const va = a[k] ?? "", vb = b[k] ?? "";
      const cmp = (typeof va === "number" && typeof vb === "number") ? va - vb : va.toString().localeCompare(vb.toString(), "tr", { numeric: true });
      return plSort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [pl.rows, plEmp, plSort]);

  return (
    <main className="content" data-testid="page-finance">
      <div className="page-head">
        <div>
          <span className="section-kicker">FİNANSAL RAPORLAR</span>
          <h1>Finans & Raporlar</h1>
          <p>Proje bazlı kâr/zarar, aylık nakit akışı ve kur yönetimi. Konsolide: <b>{currency}</b>{t.rates_missing && <em className="warn-inline" data-testid="finance-rates-missing"> · Bazı projeler için kur tanımlı değil</em>}</p>
        </div>
        <div className="head-actions">
          <div className="currency-switch" data-testid="finance-currency-switch">
            {["TRY", "USD", "EUR"].map(c => <button key={c} className={currency === c ? "selected" : ""} onClick={() => setCurrency(c)} data-testid={`finance-currency-${c.toLowerCase()}`}>{c}</button>)}
          </div>
          <button className="new-btn" onClick={pdf} data-testid="finance-pdf-button"><FileDown size={15} /> PDF Rapor</button>
        </div>
      </div>

      <CompanyScopeBar company={company} setCompany={setCompany} />

      <div className="stats-grid">
        <div className="stat"><div><div className="eyebrow">Toplam Gelir ({currency})</div><strong data-testid="finance-total-revenue">{money(t.revenue, currency)}</strong><span className="stat-detail">Anlaşılan bedel · konsolide</span></div></div>
        <div className="stat"><div><div className="eyebrow">Toplam Maliyet ({currency})</div><strong>{money(t.cost, currency)}</strong><span className="stat-detail">İş kalemi bedelleri</span></div></div>
        <div className="stat"><div><div className="eyebrow">Brüt Kâr ({currency})</div><strong className={t.profit >= 0 ? "margin big" : "loss big"}>{money(t.profit, currency)}</strong><span className="stat-detail">Gelir - Maliyet</span></div></div>
        <div className="stat"><div><div className="eyebrow">Ortalama Marj</div><strong>%{t.margin ?? 0}</strong><span className="stat-detail">{currency} bazında</span></div></div>
      </div>

      <section className="panel" data-testid="profit-loss-panel">
        <div className="panel-head">
          <div><span className="eyebrow">KÂR / ZARAR</span><h2>Proje bazında rapor</h2></div>
          <select className="filter-select" value={plEmp} onChange={(e) => setPlEmp(e.target.value)} data-testid="pl-filter-employer">
            <option value="">Tüm işverenler</option>
            {plEmployers.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="project-table">
          <div className="table-row table-head pl-row">
            <button className="th-sort" onClick={() => plToggle("project")} data-testid="pl-sort-project"><span>PROJE</span> <PlIco col="project" /></button>
            <button className="th-sort" onClick={() => plToggle("employer")} data-testid="pl-sort-employer"><span>İŞVEREN</span> <PlIco col="employer" /></button>
            <button className="th-sort" onClick={() => plToggle("revenue")} data-testid="pl-sort-revenue"><span>GELİR</span> <PlIco col="revenue" /></button>
            <button className="th-sort" onClick={() => plToggle("cost")}><span>MALİYET</span> <PlIco col="cost" /></button>
            <button className="th-sort" onClick={() => plToggle("profit")} data-testid="pl-sort-profit"><span>KÂR</span> <PlIco col="profit" /></button>
            <button className="th-sort" onClick={() => plToggle("margin")}><span>MARJ</span> <PlIco col="margin" /></button>
            <span>GELİR ({currency})</span>
          </div>
          {plRows.map((r, i) => (
            <div className="table-row pl-row" key={r.project_id} data-testid={`pl-row-${i}`}>
              <b>{r.project}</b>
              <span>{r.employer}</span>
              <b className="mono-small">{money(r.revenue, r.currency)}</b>
              <span className="mono-small">{money(r.cost, r.currency)}</span>
              <b className={r.profit >= 0 ? "margin" : "loss"}>{money(r.profit, r.currency)}</b>
              <b className={r.margin >= 0 ? "margin" : "loss"}>%{r.margin}</b>
              <span className="mono-small">{r.revenue_conv == null ? <em className="warn-inline">kur yok</em> : money(r.revenue_conv, currency)}</span>
            </div>
          ))}
          {!plRows.length && <div className="empty-hint">Rapor verisi yok.</div>}
        </div>
      </section>

      <section className="panel" data-testid="cashflow-panel">
        <div className="panel-head"><div><span className="eyebrow">NAKİT AKIŞI</span><h2>Aylık gelen / giden ({currency})</h2></div><small className="rate-note">Aya tıklayın · kimden/kime dökümü</small></div>
        <div className="project-table">
          <div className="table-row table-head cf-row"><span>AY</span><span>GELEN</span><span>GİDEN</span><span>NET</span></div>
          {cf.map((r, i) => {
            const isOpen = openMonth === r.month;
            return (
              <div className="wi-block" key={r.month}>
                <div className="table-row cf-row clickable" data-testid={`cf-row-${i}`} onClick={() => setOpenMonth(isOpen ? null : r.month)}>
                  <b>{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {r.month}</b>
                  <b className="margin mono-small">{money(r.incoming, currency)}</b>
                  <span className="loss mono-small">{money(r.outgoing, currency)}</span>
                  <b className={r.net >= 0 ? "margin" : "loss"}>{money(r.net, currency)}</b>
                </div>
                {isOpen && (
                  <div className="cf-detail" data-testid={`cf-detail-${i}`}>
                    {(r.items || []).map((it, j) => (
                      <div className="cf-item" key={j}>
                        <span className={`pay-dir ${it.direction === "incoming" ? "in" : "out"}`}>{it.direction === "incoming" ? "Gelen" : "Giden"}</span>
                        <b>{it.direction === "incoming" ? `${it.party} →` : `→ ${it.party}`}</b>
                        <span className="muted-cell">{it.project_name || "—"} · {it.stage}</span>
                        <span className="mono-small">{it.date}</span>
                        <b className={`mono-small ${it.direction === "incoming" ? "margin" : "loss"}`}>{money(it.amount, currency)}</b>
                      </div>
                    ))}
                    {!(r.items || []).length && <div className="empty-hint">Bu ay için ödeme detayı yok.</div>}
                  </div>
                )}
              </div>
            );
          })}
          {!cf.length && <div className="empty-hint">Nakit akışı verisi yok (tarihli ve "Ödendi" durumundaki ödemeler gerekir).</div>}
        </div>
      </section>

      <RatesPanel onChange={() => setTick((x) => x + 1)} />

      {vat && (
        <section className="panel" data-testid="vat-report-panel">
          <div className="panel-head">
            <div><span className="eyebrow">KDV ÖZETİ</span><h2>Hesaplanan / İndirilecek KDV ({currency})</h2></div>
            <small className="rate-note">Girilen bedeller KDV hariç · ödeme bazında dahil/hariç ayrımı</small>
          </div>
          <div className="stats-grid">
            <div className="stat"><div><div className="eyebrow">Tahsil Edilen KDV</div><strong className="margin" data-testid="vat-incoming">{money(vat.totals.incoming.vat, currency)}</strong><span className="stat-detail">İşveren tahsilatları · {vat.totals.incoming.count} ödeme</span></div></div>
            <div className="stat"><div><div className="eyebrow">Ödenen KDV (İndirilecek)</div><strong className="loss" data-testid="vat-outgoing">{money(vat.totals.outgoing.vat, currency)}</strong><span className="stat-detail">Alt yüklenici ödemeleri · {vat.totals.outgoing.count} ödeme</span></div></div>
            <div className="stat"><div><div className="eyebrow">Ödenecek / Devreden KDV</div><strong className={vat.net_vat_payable >= 0 ? "margin big" : "loss big"} data-testid="vat-net">{money(vat.net_vat_payable, currency)}</strong><span className="stat-detail">{vat.net_vat_payable >= 0 ? "Devlete ödenecek" : "Devreden (iade/indirim)"}</span></div></div>
            <div className="stat"><div><div className="eyebrow">Net Matrah Farkı</div><strong>{money((vat.totals.incoming.net || 0) - (vat.totals.outgoing.net || 0), currency)}</strong><span className="stat-detail">Gelen net − giden net</span></div></div>
          </div>
          <div className="project-table">
            <div className="table-row table-head vat-tot-row"><span>YÖN</span><span>NET</span><span>KDV</span><span>BRÜT</span></div>
            {["incoming", "outgoing"].map((d) => (
              <div className="table-row vat-tot-row" key={d} data-testid={`vat-tot-${d}`}>
                <b>{d === "incoming" ? "Gelen (Tahsilat)" : "Giden (Ödeme)"}</b>
                <span className="mono-small">{money(vat.totals[d].net, currency)}</span>
                <b className={`mono-small ${d === "incoming" ? "margin" : "loss"}`}>{money(vat.totals[d].vat, currency)}</b>
                <span className="mono-small">{money(vat.totals[d].gross, currency)}</span>
              </div>
            ))}
          </div>
          {vat.rates_missing && <div className="empty-hint">Bazı ödemeler için kur tanımlı değil; o tutarlar KDV özetine dahil edilemedi.</div>}
        </section>
      )}
    </main>
  );
}
